// ProjectSettingsDialog.tsx — Display preferences, 3D viewer, and embed library
// Structural settings (concrete, rebar, joints) live on the Project Details page

import { useState } from 'react';
import DialogBase from './DialogBase';
import { useShallowStore } from '../store/useStore';
import type { ProjectSettings, EmbedTypeEntry, EmbedCategory } from '../types/panelvar';
import { defaultProjectSettings } from '../types/panelvar';
import { BUILTIN_EMBEDS } from '../utils/embedLibrary';

const EMPTY_FORM = {
  id: '', label: '', desc: '',
  // weld fields
  width: '', height: '', thick: '', studs: '', studDia: '',
  // brace + pick shared
  size: '', embedLen: '', capacity: '',
};

export default function ProjectSettingsDialog() {
  const { settings, updateProject } = useShallowStore(s => ({
    settings:      s.project?.settings ?? defaultProjectSettings(),
    updateProject: s.updateProject,
  }));

  // ── Display ────────────────────────────────────────────────────────────────
  const [measurementSystem, setMeasurementSystem] = useState(settings.measurementSystem);
  const [precision,  setPrecision]  = useState(settings.precision);
  const [paperSize,  setPaperSize]  = useState(settings.paperSize);
  const [paperOri,   setPaperOri]   = useState(settings.paperOrientation);
  const [titleBlock, setTitleBlock] = useState(settings.titleBlock);

  // ── Snapshots ──────────────────────────────────────────────────────────────
  const [maxSnap,   setMaxSnap]   = useState(settings.maxSnapshots);

  // ── Developer mode ─────────────────────────────────────────────────────────
  const [devMode, setDevMode] = useState(settings.developerMode ?? false);

  // ── Embed library ─────────────────────────────────────────────────────────
  const [embedTypes, setEmbedTypes] = useState<EmbedTypeEntry[]>(settings.embedTypes ?? []);
  const [embedTab,   setEmbedTab]   = useState<EmbedCategory>('weld');
  const [showAdd,    setShowAdd]    = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [formErr,    setFormErr]    = useState('');

  function handleOk() {
    const patch: ProjectSettings = {
      ...settings,
      measurementSystem,
      precision,
      paperSize,
      paperOrientation: paperOri,
      titleBlock,
      maxSnapshots:     maxSnap,
      developerMode:    devMode,
      embedTypes:       embedTypes.length > 0 ? embedTypes : undefined,
    };
    updateProject({ settings: patch });
  }

  function numRow(label: string, value: number, setter: (v: number) => void,
                  unit: string, step: number, min = 0) {
    return (
      <div className="dialog-row" key={label}>
        <label className="dialog-label">{label}</label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <input
            className="dialog-input"
            type="number"
            value={value}
            step={step}
            min={min}
            onChange={e => setter(Number(e.target.value))}
            style={{ width: 100 }}
          />
          <span style={{ fontSize: 12, opacity: 0.7 }}>{unit}</span>
        </div>
      </div>
    );
  }

  function setF(key: keyof typeof EMPTY_FORM, val: string) {
    setForm(prev => ({ ...prev, [key]: val }));
    setFormErr('');
  }

  function addCustom() {
    const id = form.id.trim();
    if (!id) { setFormErr('ID is required'); return; }
    const allIds = new Set([...BUILTIN_EMBEDS.map(e => e.id), ...embedTypes.map(e => e.id)]);
    if (allIds.has(id)) { setFormErr(`ID "${id}" is already in use`); return; }

    const mark = form.label.trim() || id;
    let data: EmbedTypeEntry['data'];
    if (embedTab === 'weld') {
      data = {
        mark, type: 'plate' as const,
        width:   parseFloat(form.width)   || 6,
        height:  parseFloat(form.height)  || 6,
        thick:   parseFloat(form.thick)   || 0.375,
        studs:   parseFloat(form.studs)   || 4,
        studDia: parseFloat(form.studDia) || 0.75,
      };
    } else if (embedTab === 'brace') {
      data = {
        mark, type: 'strongback' as const,
        size:     parseFloat(form.size)     || 1.25,
        embedLen: parseFloat(form.embedLen) || 12,
      };
    } else {
      data = {
        mark,
        size:     parseFloat(form.size)     || 1.25,
        capacity: parseFloat(form.capacity) || 5,
        embedLen: parseFloat(form.embedLen) || 12,
        load: 0,
      };
    }

    setEmbedTypes(prev => [...prev, {
      id, label: mark, desc: form.desc.trim(), category: embedTab, data,
    }]);
    setForm(EMPTY_FORM);
    setFormErr('');
    setShowAdd(false);
  }

  const customForTab  = embedTypes.filter(e => e.category === embedTab);
  const builtinForTab = BUILTIN_EMBEDS.filter(e => e.category === embedTab);
  const tabLabel = (cat: EmbedCategory) =>
    cat === 'weld' ? 'Weld / Embeds' : cat === 'brace' ? 'Brace Inserts' : 'Pick Inserts';

  return (
    <DialogBase title="Project Settings" onOk={handleOk} okLabel="Save" width={540}>
      <div className="dialog-section-title">Display</div>
      <div className="dialog-row">
        <label className="dialog-label">Measurement</label>
        <select className="dialog-input" value={measurementSystem}
          onChange={e => setMeasurementSystem(e.target.value as 'imperial' | 'metric')}
          style={{ width: 160 }}>
          <option value="imperial">Imperial (ft-in)</option>
          <option value="metric">Metric (mm)</option>
        </select>
      </div>
      <div className="dialog-row">
        <label className="dialog-label">Precision</label>
        <select className="dialog-input" value={precision}
          onChange={e => setPrecision(e.target.value as ProjectSettings['precision'])}
          style={{ width: 160 }}>
          <option value="1/2">1/2"</option>
          <option value="1/4">1/4"</option>
          <option value="1/8">1/8"</option>
          <option value="1/16">1/16"</option>
          <option value="1/32">1/32"</option>
        </select>
      </div>
      <div className="dialog-row">
        <label className="dialog-label">Paper Size</label>
        <select className="dialog-input" value={paperSize}
          onChange={e => setPaperSize(e.target.value as ProjectSettings['paperSize'])}
          style={{ width: 220 }}>
          <optgroup label="Office">
            <option value="Letter">Letter (8.5×11")</option>
            <option value="Legal">Legal (8.5×14")</option>
            <option value="Tabloid">Tabloid / 11×17 (11×17")</option>
          </optgroup>
          <optgroup label="Engineering">
            <option value="ARCH-D">ARCH D (24×36")</option>
            <option value="ARCH-E">ARCH E (36×48")</option>
            <option value="ANSI-D">ANSI D (22×34")</option>
            <option value="ANSI-E">ANSI E (34×44")</option>
          </optgroup>
          <optgroup label="ISO">
            <option value="A1">A1 (594×841mm)</option>
            <option value="A0">A0 (841×1189mm)</option>
          </optgroup>
        </select>
      </div>
      <div className="dialog-row">
        <label className="dialog-label">Orientation</label>
        <select className="dialog-input" value={paperOri}
          onChange={e => setPaperOri(e.target.value as 'portrait' | 'landscape')}
          style={{ width: 160 }}>
          <option value="portrait">Portrait</option>
          <option value="landscape">Landscape</option>
        </select>
      </div>
      <div className="dialog-row">
        <label className="dialog-label">Title Block</label>
        <select className="dialog-input" value={titleBlock}
          onChange={e => setTitleBlock(e.target.value)}
          style={{ width: 160 }}>
          <option value="standard">Standard</option>
          <option value="full">Full</option>
          <option value="none">None</option>
        </select>
      </div>

      <div className="dialog-section-title">Snapshots</div>
      {numRow('Max Snapshots',  maxSnap, setMaxSnap, '', 1, 1)}

      <div className="dialog-section-title">Developer</div>
      <div className="dialog-row">
        <label className="dialog-label">Developer Mode</label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={devMode} onChange={e => setDevMode(e.target.checked)} />
          <span style={{ fontSize: 12, opacity: 0.7 }}>Show experimental features</span>
        </label>
      </div>

      {/* ── Embed Library ────────────────────────────────────────────────── */}
      <div className="dialog-section-title">Embed Library</div>
      <p style={{ fontSize: 11, color: '#888', marginBottom: 8 }}>
        Custom embed types appear in the quick-select palette alongside built-ins.
        Override a built-in by using the same ID.
      </p>

      {/* Category tabs */}
      <div className="embed-lib-tabs">
        {(['weld', 'brace', 'pick'] as EmbedCategory[]).map(cat => {
          const count = embedTypes.filter(e => e.category === cat).length;
          return (
            <button
              key={cat}
              type="button"
              className={`embed-lib-tab${embedTab === cat ? ' active' : ''}`}
              onClick={() => { setEmbedTab(cat); setShowAdd(false); setFormErr(''); setForm(EMPTY_FORM); }}
            >
              {tabLabel(cat)}
              {count > 0 && <span className="embed-lib-badge">{count}</span>}
            </button>
          );
        })}
      </div>

      {/* Built-in chips (read-only reference) */}
      <div className="embed-lib-section-label">
        Built-in ({builtinForTab.length}) — read-only
      </div>
      <div className="embed-lib-chips">
        {builtinForTab.map(e => (
          <span key={e.id} className="embed-lib-chip" title={e.desc}>{e.label}</span>
        ))}
      </div>

      {/* Custom entries list */}
      <div className="embed-lib-section-label" style={{ marginTop: 10 }}>
        Custom ({customForTab.length})
      </div>

      {customForTab.length === 0 && !showAdd && (
        <div style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic', marginBottom: 6 }}>
          No custom types yet.
        </div>
      )}

      {customForTab.map(entry => (
        <div key={entry.id} className="embed-lib-row">
          <span className="embed-lib-row-id">{entry.label}</span>
          <span className="embed-lib-row-desc">{entry.desc || <em style={{ color: '#bbb' }}>no description</em>}</span>
          <button
            type="button"
            className="embed-lib-delete"
            title="Remove"
            onClick={() => setEmbedTypes(prev => prev.filter(e => e.id !== entry.id))}
          >✕</button>
        </div>
      ))}

      {/* Add custom form */}
      {!showAdd ? (
        <button type="button" className="embed-lib-add-btn" onClick={() => setShowAdd(true)}>
          + Add Custom Type
        </button>
      ) : (
        <div className="embed-lib-form">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px 10px' }}>
            <div>
              <div className="embed-lib-field-label">ID *</div>
              <input className="dialog-input" placeholder="e.g. 7A" value={form.id} onChange={e => setF('id', e.target.value)} />
            </div>
            <div>
              <div className="embed-lib-field-label">Label</div>
              <input className="dialog-input" placeholder="Display name" value={form.label} onChange={e => setF('label', e.target.value)} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="embed-lib-field-label">Description</div>
              <input className="dialog-input" style={{ width: '100%' }} placeholder="e.g. 7×7 Weld Plate ½″t  4-¾″ studs" value={form.desc} onChange={e => setF('desc', e.target.value)} />
            </div>
          </div>

          {/* Dynamic fields by category */}
          {embedTab === 'weld' && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px 10px', marginTop: 6 }}>
              {[
                { key: 'width',   label: 'Plate W"' },
                { key: 'height',  label: 'Plate H"' },
                { key: 'thick',   label: 'Thick"'   },
                { key: 'studs',   label: '# Studs'  },
                { key: 'studDia', label: 'Stud Dia"'},
              ].map(f => (
                <div key={f.key}>
                  <div className="embed-lib-field-label">{f.label}</div>
                  <input className="dialog-input" type="number" min={0} step={0.125}
                    value={form[f.key as keyof typeof EMPTY_FORM]}
                    onChange={e => setF(f.key as keyof typeof EMPTY_FORM, e.target.value)} />
                </div>
              ))}
            </div>
          )}

          {(embedTab === 'brace' || embedTab === 'pick') && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px 10px', marginTop: 6 }}>
              <div>
                <div className="embed-lib-field-label">Size (dia)"</div>
                <input className="dialog-input" type="number" min={0} step={0.125} value={form.size} onChange={e => setF('size', e.target.value)} />
              </div>
              <div>
                <div className="embed-lib-field-label">Embed Len"</div>
                <input className="dialog-input" type="number" min={0} step={1} value={form.embedLen} onChange={e => setF('embedLen', e.target.value)} />
              </div>
              {embedTab === 'pick' && (
                <div>
                  <div className="embed-lib-field-label">Capacity (T)</div>
                  <input className="dialog-input" type="number" min={0} step={1} value={form.capacity} onChange={e => setF('capacity', e.target.value)} />
                </div>
              )}
            </div>
          )}

          {formErr && <div style={{ color: '#c62828', fontSize: 11, marginTop: 6 }}>{formErr}</div>}

          <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 10 }}>
            <button type="button" className="btn-secondary"
              onClick={() => { setShowAdd(false); setForm(EMPTY_FORM); setFormErr(''); }}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={addCustom}>
              Add
            </button>
          </div>
        </div>
      )}
    </DialogBase>
  );
}
